import { useState, useEffect, useCallback, useRef } from 'react'
import { useToast } from '@chakra-ui/react'
import api from '../utils/api'
import { storage } from '../utils/storage'
import { NOTIFICATION_SETTINGS, type NotificationType } from '../config/notifications'
// import { useErrorHandler } from './useErrorHandler' // 暫時註解

export interface Notification {
    id: number
    userId: number
    workflowId?: number
    type: NotificationType
    title: string
    message: string
    data?: any
    isRead: boolean
    createdAt: string
}

export interface NotificationEvent {
    type: 'connected' | 'heartbeat' | 'notification'
    data?: Notification
    unreadCount?: number
    message?: string
    timestamp: string
    userId?: string
}

export function useNotifications() {
    const [notifications, setNotifications] = useState<Notification[]>([])
    const [unreadCount, setUnreadCount] = useState(0)
    const [isConnected, setIsConnected] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const eventSourceRef = useRef<EventSource | null>(null)
    const toast = useToast()
    // const { handleError } = useErrorHandler() // 暫時註解，待完善錯誤處理

    // 獲取通知列表
    const fetchNotifications = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await api.get('/notifications')
            setNotifications(response.data.data)
            setUnreadCount(response.data.unreadCount)
        } catch (err: any) {
            setError(err.message || '獲取通知失敗')
        } finally {
            setLoading(false)
        }
    }, [])

    // 獲取未讀通知
    const fetchUnreadNotifications = useCallback(async () => {
        try {
            const response = await api.get('/notifications/unread')
            setUnreadCount(response.data.unreadCount)
            return response.data.data
        } catch (err: any) {
            console.error('獲取未讀通知失敗:', err)
            return []
        }
    }, [])

    // 標記通知為已讀
    const markAsRead = useCallback(async (notificationId: number) => {
        try {
            await api.put(`/notifications/${notificationId}/read`)

            // 更新本地狀態
            setNotifications(prev =>
                prev.map(n =>
                    n.id === notificationId ? { ...n, isRead: true } : n
                )
            )
            setUnreadCount(prev => Math.max(0, prev - 1))
        } catch (err: any) {
            console.error('標記通知失敗:', err)
        }
    }, [])

    // 標記所有通知為已讀
    const markAllAsRead = useCallback(async () => {
        try {
            await api.put('/notifications/read-all')

            // 更新本地狀態
            setNotifications(prev =>
                prev.map(n => ({ ...n, isRead: true }))
            )
            setUnreadCount(0)
        } catch (err: any) {
            console.error('標記所有通知失敗:', err)
        }
    }, [])

    // 建立 SSE 連接
    const connectToNotifications = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close()
        }

        try {
            // 獲取 token
            const token = storage.getItem('accessToken')
            if (!token) {
                console.warn('No access token found')
                return
            }

            // 建立 SSE 連接，通過 URL 參數傳遞 token
            const eventSource = new EventSource(
                `${api.defaults.baseURL}/notifications/stream?token=${encodeURIComponent(token)}`
            )

            eventSource.onopen = () => {
                console.log('通知推送連接已建立')
                setIsConnected(true)
                setError(null)
            }

            eventSource.onmessage = (event) => {
                try {
                    const eventData: NotificationEvent = JSON.parse(event.data)

                    switch (eventData.type) {
                        case 'connected':
                            console.log('通知推送已連接:', eventData.message)
                            break

                        case 'heartbeat':
                            // 心跳包，保持連接
                            break

                        case 'notification':
                            if (eventData.data) {
                                // 新通知到達
                                setNotifications(prev => [eventData.data!, ...prev])
                                setUnreadCount(eventData.unreadCount || 0)

                                // 顯示 toast 通知（使用配置）
                                toast({
                                    title: eventData.data.title,
                                    description: eventData.data.message,
                                    status: 'info',
                                    ...NOTIFICATION_SETTINGS.toast,
                                })
                            }
                            break
                    }
                } catch (err) {
                    console.error('解析通知事件失敗:', err)
                }
            }

            eventSource.onerror = (event) => {
                console.error('通知推送連接錯誤:', event)
                setIsConnected(false)
                setError('通知推送連接中斷')

                // 嘗試重新連接（使用配置的延遲）
                setTimeout(() => {
                    if (eventSourceRef.current?.readyState === EventSource.CLOSED) {
                        connectToNotifications()
                    }
                }, NOTIFICATION_SETTINGS.sse.reconnectDelay)
            }

            eventSourceRef.current = eventSource
        } catch (err) {
            console.error('建立通知推送連接失敗:', err)
            setError('無法建立通知推送連接')
        }
    }, [toast])

    // 斷開 SSE 連接
    const disconnectFromNotifications = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close()
            eventSourceRef.current = null
            setIsConnected(false)
        }
    }, [])

    // 組件掛載時建立連接
    useEffect(() => {
        fetchNotifications()
        connectToNotifications()

        return () => {
            disconnectFromNotifications()
        }
    }, [fetchNotifications, connectToNotifications, disconnectFromNotifications])

    return {
        notifications,
        unreadCount,
        isConnected,
        loading,
        error,
        fetchNotifications,
        fetchUnreadNotifications,
        markAsRead,
        markAllAsRead,
        connectToNotifications,
        disconnectFromNotifications,
    }
}